import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from '@mui/material';

// Helper function to get current month's start and end dates
const getCurrentMonthDateRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  console.log("startOfMonth:", startOfMonth, "endOfMonth:", endOfMonth);

  // Format as YYYY-MM-DD with local timezone (not UTC)
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(startOfMonth),
    endDate: formatDate(endOfMonth)
  };
};

export default function BillingTrackTables() {
  const { userId } = useParams();
  const [activeTab, setActiveTab] = useState('summary');
  const [heygenReports, setHeygenReports] = useState([]);
  const [openaiReports, setOpenaiReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [filteredSummaryData, setFilteredSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('heygenMinutes');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [clientUrl, setClientUrl] = useState('');

  const monthRange = getCurrentMonthDateRange();
  const [summaryStartDate, setSummaryStartDate] = useState(monthRange.startDate);
  const [summaryEndDate, setSummaryEndDate] = useState(monthRange.endDate);
  const [tempSummaryStartDate, setTempSummaryStartDate] = useState(monthRange.startDate);
  const [tempSummaryEndDate, setTempSummaryEndDate] = useState(monthRange.endDate);

  const [companyDateFilters, setCompanyDateFilters] = useState({});
  const [companyFilteredData, setCompanyFilteredData] = useState({});

  // Validation states
  const [dateError, setDateError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (activeTab === 'heygen') {
      fetchHeygenReports();
    } else if (activeTab === 'openai') {
      fetchOpenAIReports();
    } else if (activeTab === 'summary') {
      fetchSummaryData();
    }
  }, [activeTab, startDate, endDate, clientUrl, summaryStartDate, summaryEndDate, companies]);

  // Update filtered summary data when base data or filters change
  useEffect(() => {
    updateFilteredSummaryData();
  }, [summaryData, companyFilteredData]);

  const updateFilteredSummaryData = () => {
    if (Object.keys(companyFilteredData).length === 0) {
      setFilteredSummaryData(summaryData);
      return;
    }

    const filteredData = summaryData.map(company => {
      const filteredCompanyData = companyFilteredData[company.company_name];
      if (filteredCompanyData) {
        return {
          ...company,
          heygenMinutes: filteredCompanyData.heygenMinutes,
          openaiTokens: filteredCompanyData.openaiTokens,
          heygenSessions: filteredCompanyData.heygenSessions,
          openaiRequests: filteredCompanyData.openaiRequests,
        };
      }
      return company;
    });

    setFilteredSummaryData(filteredData);
  };

  // Date validation functions
  const validateDates = (start, end, fieldName = 'End Date') => {
    if (start && end) {
      const startDateObj = new Date(start);
      const endDateObj = new Date(end);

      if (endDateObj < startDateObj) {
        setDateError(`${fieldName} must be greater than or equal to Start Date`);
        setSnackbarOpen(true);
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const getMinEndDate = startDate => {
    return startDate || '';
  };

  const handleStartDateChange = (date, setStartDateFunc, setEndDateFunc, endDateValue) => {
    setStartDateFunc(date);

    // If end date exists and is before the new start date, clear it
    if (endDateValue && date && new Date(endDateValue) < new Date(date)) {
      setEndDateFunc('');
      setDateError('End Date was cleared because it was before Start Date');
      setSnackbarOpen(true);
    }
  };

  const handleEndDateChange = (date, startDateValue, setEndDateFunc) => {
    setEndDateFunc(date);
    validateDates(startDateValue, date);
  };

  /*const handleCompanyStartDateChange = (companyName, value) => {
    const currentFilters = companyDateFilters[companyName] || {};

    // If end date exists and is before the new start date, clear it
    if (currentFilters.endDate && value && new Date(currentFilters.endDate) < new Date(value)) {
      setCompanyDateFilters(prev => ({
        ...prev,
        [companyName]: {
          ...currentFilters,
          startDate: value,
          endDate: '',
        },
      }));
      setDateError(`End Date for ${companyName} was cleared because it was before Start Date`);
      setSnackbarOpen(true);
    } else {
      setCompanyDateFilters(prev => ({
        ...prev,
        [companyName]: {
          ...currentFilters,
          startDate: value,
        },
      }));
    }
  };
  */

  const handleCompanyStartDateChange = (companyName, value) => {
    const currentFilters = companyDateFilters[companyName] || {};

    // If end date exists and is before the new start date, clear it
    if (currentFilters.endDate && value && new Date(currentFilters.endDate) < new Date(value)) {
      setCompanyDateFilters(prev => ({
        ...prev,
        [companyName]: {
          ...currentFilters,
          startDate: value,
          endDate: '',
        },
      }));
      setDateError(`End Date for ${companyName} was cleared because it was before Start Date`);
      setSnackbarOpen(true);

      // Remove filtered data when end date is cleared
      setCompanyFilteredData(prev => {
        const newData = { ...prev };
        delete newData[companyName];
        return newData;
      });
    } else {
      const newFilters = {
        ...currentFilters,
        startDate: value,
      };

      setCompanyDateFilters(prev => ({
        ...prev,
        [companyName]: newFilters,
      }));

      // If both dates are set after the change, fetch data immediately
      if (newFilters.startDate && newFilters.endDate) {
        fetchCompanyUsage(companyName, newFilters.startDate, newFilters.endDate);
      } else if (!newFilters.startDate && !newFilters.endDate) {
        // If both dates are cleared, remove filtered data
        setCompanyFilteredData(prev => {
          const newData = { ...prev };
          delete newData[companyName];
          return newData;
        });
      }
    }
  };

  const handleCompanyEndDateChange = (companyName, value) => {
    const currentFilters = companyDateFilters[companyName] || {};
    const newFilters = {
      ...currentFilters,
      endDate: value,
    };

    setCompanyDateFilters(prev => ({
      ...prev,
      [companyName]: newFilters,
    }));

    // Validate dates
    if (currentFilters.startDate && value) {
      validateDates(currentFilters.startDate, value, `End Date for ${companyName}`);
    }

    // If both dates are set, fetch data
    if (newFilters.startDate && newFilters.endDate) {
      fetchCompanyUsage(companyName, newFilters.startDate, newFilters.endDate);
    } else if (!newFilters.startDate && !newFilters.endDate) {
      // If both dates are cleared, remove filtered data
      setCompanyFilteredData(prev => {
        const newData = { ...prev };
        delete newData[companyName];
        return newData;
      });
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/auth/users');
      setCompanies(response.data.users || response.data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchSummaryData = async () => {
    try {
      setLoading(true);
      const [heygenResponse, openaiResponse] = await Promise.all([
        axios.get(`/api/heygen/${userId}`, {
          params: { startDate: summaryStartDate, endDate: summaryEndDate },
        }),
        axios.get(`/api/openaireport/${userId}`, {
          params: { startDate: summaryStartDate, endDate: summaryEndDate },
        }),
      ]);

      const heygenData = heygenResponse.data.reports || [];
      const openaiData = openaiResponse.data.reports || [];

      const companyMap = {};
      companies.forEach(company => {
        const identifier = company.client_url || company.email;
        if (identifier) {
          companyMap[identifier] = {
            company_name: company.company_name || company.name || company.email,
            originalIdentifier: identifier,
          };
        }
      });

      // Group data by company name
      const dataByCompany = {};

      // Process Heygen data
      heygenData.forEach(report => {
        const companyUrl = report.client_url || 'Unknown';
        const companyInfo = companyMap[companyUrl] || {
          company_name: companyUrl,
          originalIdentifier: companyUrl,
        };

        if (!dataByCompany[companyInfo.company_name]) {
          dataByCompany[companyInfo.company_name] = {
            company_name: companyInfo.company_name,
            originalIdentifier: companyInfo.originalIdentifier,
            heygenMinutes: 0,
            openaiTokens: 0,
            heygenSessions: 0,
            openaiRequests: 0,
          };
        }
        dataByCompany[companyInfo.company_name].heygenMinutes +=
          parseFloat(report.total_minutes) || 0;
        dataByCompany[companyInfo.company_name].heygenSessions += 1;
        dataByCompany[companyInfo.company_name].liveStreamingMint = parseFloat(report.liveStreamingMint) || 0;
      });

      // Process OpenAI data
      openaiData.forEach(report => {
        const companyUrl = report.client_url || 'Unknown';
        const companyInfo = companyMap[companyUrl] || {
          company_name: companyUrl,
          originalIdentifier: companyUrl,
        };

        if (!dataByCompany[companyInfo.company_name]) {
          dataByCompany[companyInfo.company_name] = {
            company_name: companyInfo.company_name,
            originalIdentifier: companyInfo.originalIdentifier,
            heygenMinutes: 0,
            openaiTokens: 0,
            heygenSessions: 0,
            openaiRequests: 0,
            monthlyOpenAiTotalToken: 0,
            liveStreamingMint: 0,
          };
        }
        dataByCompany[companyInfo.company_name].openaiTokens += parseInt(report.total_tokens) || 0;
        dataByCompany[companyInfo.company_name].openaiRequests += 1;
        dataByCompany[companyInfo.company_name].monthlyOpenAiTotalToken = parseInt(report.monthlyOpenAiTotalToken) || 0;
        dataByCompany[companyInfo.company_name].liveStreamingMint = parseFloat(report.liveStreamingMint) || 0;
      });

      const summaryArray = Object.values(dataByCompany).sort(
        (a, b) => b.heygenMinutes + b.openaiTokens - (a.heygenMinutes + a.openaiTokens)
      );

      setSummaryData(summaryArray);
      setFilteredSummaryData(summaryArray);
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Apply global summary filter
  const applyGlobalSummaryFilter = () => {
    if (validateDates(tempSummaryStartDate, tempSummaryEndDate)) {
      setSummaryStartDate(tempSummaryStartDate);
      setSummaryEndDate(tempSummaryEndDate);
      // Clear individual company filters when global filter is applied
      setCompanyDateFilters({});
      setCompanyFilteredData({});
    }
  };

  // Clear global summary filter
  const clearGlobalSummaryFilter = () => {
    setSummaryStartDate('');
    setSummaryEndDate('');
    setTempSummaryStartDate('');
    setTempSummaryEndDate('');
    setCompanyDateFilters({});
    setCompanyFilteredData({});
    setDateError('');
  };

  // Fetch individual company usage
  const fetchCompanyUsage = async (companyName, startDate, endDate) => {
    try {
      // Find the original identifier for this company
      const companyData = summaryData.find(item => item.company_name === companyName);
      if (!companyData) {
        console.error(`Company data not found for: ${companyName}`);
        return;
      }

      const companyIdentifier = companyData.originalIdentifier;

      const [heygenResponse, openaiResponse] = await Promise.all([
        axios.get(`/api/heygen/${userId}`, {
          params: {
            startDate,
            endDate,
            client_url: companyIdentifier,
          },
        }),
        axios.get(`/api/openaireport/${userId}`, {
          params: {
            startDate,
            endDate,
            client_url: companyIdentifier,
          },
        }),
      ]);

      const heygenData = heygenResponse.data.reports || [];
      const openaiData = openaiResponse.data.reports || [];

      const heygenMinutes = heygenData.reduce(
        (sum, report) => sum + (parseFloat(report.total_minutes) || 0),
        0
      );

      const openaiTokens = openaiData.reduce(
        (sum, report) => sum + (parseInt(report.total_tokens) || 0),
        0
      );

      const filteredData = {
        heygenMinutes,
        openaiTokens,
        heygenSessions: heygenData.length,
        openaiRequests: openaiData.length,
      };

      // Update the filtered data for this company
      setCompanyFilteredData(prev => ({
        ...prev,
        [companyName]: filteredData,
      }));
    } catch (error) {
      console.error(`Error fetching usage for company ${companyName}:`, error);
      // Set zero data on error
      setCompanyFilteredData(prev => ({
        ...prev,
        [companyName]: {
          heygenMinutes: 0,
          openaiTokens: 0,
          heygenSessions: 0,
          openaiRequests: 0,
        },
      }));
    }
  };

  const fetchHeygenReports = async () => {
    try {
      const response = await axios.get(`/api/heygen/${userId}`, {
        params: { startDate, endDate, client_url: clientUrl },
      });
      setHeygenReports(response.data.reports);
    } catch (error) {
      console.error('Error fetching Heygen reports:', error);
    }
  };

  const fetchOpenAIReports = async () => {
    try {
      const response = await axios.get(`/api/openaireport/${userId}`, {
        params: { startDate, endDate, client_url: clientUrl },
      });
      setOpenaiReports(response.data.reports);
    } catch (error) {
      console.error('Error fetching OpenAI reports:', error);
    }
  };

  const formatTimestamp = timestamp => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  // Prepare chart data with better formatting - use filtered data
  const prepareChartData = () => {
    return filteredSummaryData
      .filter(company => company.heygenMinutes > 0 || company.openaiTokens > 0)
      .slice(0, 6)
      .map(company => ({
        name:
          company.company_name?.length > 10
            ? company.company_name.substring(0, 10) + '...'
            : company.company_name,
        heygenMinutes: parseFloat(company.heygenMinutes.toFixed(2)),
        openaiTokens: Math.round(company.openaiTokens / 1000), // Convert to thousands for better scale
        fullName: company.company_name,
      }));
  };

  // Summary statistics - use filtered data
  const getSummaryStats = () => {
    const totalMinutes = filteredSummaryData.reduce(
      (sum, company) => sum + company.heygenMinutes,
      0
    );
    const totalTokens = filteredSummaryData.reduce((sum, company) => sum + company.openaiTokens, 0);
    const totalCompanies = filteredSummaryData.length;
    const activeCompanies = filteredSummaryData.filter(
      company => company.heygenMinutes > 0 || company.openaiTokens > 0
    ).length;

    return { totalMinutes, totalTokens, totalCompanies, activeCompanies };
  };

  const summaryStats = getSummaryStats();
  const chartData = prepareChartData();

  // Get chart configuration based on selected type
  const getChartConfig = () => {
    if (chartType === 'heygenMinutes') {
      return {
        barDataKey: 'heygenMinutes',
        pieDataKey: 'heygenMinutes',
        barColor: '#8884d8',
        barName: 'Heygen Minutes',
        tooltipSuffix: ' minutes',
        pieTooltipFormatter: value => [`${value} minutes`, 'Heygen Minutes'],
        chartTitle: 'Top Companies - Heygen Minutes',
        pieChartTitle: 'Heygen Minutes Distribution',
      };
    } else {
      return {
        barDataKey: 'openaiTokens',
        pieDataKey: 'openaiTokens',
        barColor: '#82ca9d',
        barName: 'OpenAI Tokens (K)',
        tooltipSuffix: 'K tokens',
        pieTooltipFormatter: value => [`${value}K tokens`, 'OpenAI Tokens'],
        chartTitle: 'Top Companies - OpenAI Tokens',
        pieChartTitle: 'OpenAI Tokens Distribution',
      };
    }
  };





  // Custom styles for DataTable
  const customStyles = {
    rows: {
      style: {
        minHeight: '60px',
        paddingTop: '8px',
        paddingBottom: '8px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e0e0e0',
        },
        '&:hover': {
          backgroundColor: '#f8f9fa',
          transition: 'background-color 0.3s ease',
        },
      },
    },
    headCells: {
      style: {
        paddingTop: '12px',
        paddingBottom: '12px',
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        fontSize: '14px',
      },
    },
    cells: {
      style: {
        paddingTop: '8px',
        paddingBottom: '8px',
        fontSize: '13px',
      },
    },
  };

  // Clear company filter function
  const clearCompanyFilter = companyName => {
    setCompanyDateFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[companyName];
      return newFilters;
    });

    setCompanyFilteredData(prev => {
      const newData = { ...prev };
      delete newData[companyName];
      return newData;
    });
  };

  // Columns for Summary
  const summaryColumns = [
    {
      name: 'Company Name',
      selector: row => row.company_name,
      sortable: true,
      wrap: true,
      width: '250px',
      cell: row => (
        <div>
          <strong style={{ fontSize: '14px' }}>{row.company_name}</strong>
        </div>
      ),
    },
    {
      name: 'Client URL',
      selector: row => row.originalIdentifier || row.company_name,
      sortable: true,
      wrap: true,
      width: '250px',
      cell: row => (
        <div>
          <p style={{ fontSize: '14px' }}>{row.originalIdentifier || row.company_name}</p>
        </div>
      ),
    },
    {
      name: 'Date Range',
      width: '300px',
      cell: row => {
        const filters = companyDateFilters[row.company_name] || {};
        return (
          <div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={e => handleCompanyStartDateChange(row.company_name, e.target.value)}
                className="form-control form-control-sm"
                style={{ fontSize: '12px' }}
              />
              <input
                type="date"
                value={filters.endDate || ''}
                min={getMinEndDate(filters.startDate)}
                onChange={e => handleCompanyEndDateChange(row.company_name, e.target.value)}
                className="form-control form-control-sm"
                style={{
                  fontSize: '12px',
                  border:
                    dateError && dateError.includes(row.company_name)
                      ? '1px solid red'
                      : '1px solid #ced4da',
                }}
              />
            </div>
            {(filters.startDate || filters.endDate) && (
              <div className="mt-2 d-flex align-items-center">
                <small className="text-muted" style={{ fontSize: '15px', fontWeight: '700' }}>
                  {filters.startDate || 'Any'} to {filters.endDate || 'Any'}
                </small>
                <button
                  onClick={() => clearCompanyFilter(row.company_name)}
                  className="ml-1"
                  style={{ all: 'unset', fontSize: '15px', padding: '4px 4px', cursor: 'pointer' }}
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: 'Heygen Minutes',
      selector: row => row.heygenMinutes,
      sortable: true,
      right: true,
      width: '200px',
      cell: row => {
        const hasFilter =
          companyDateFilters[row.company_name] &&
          (companyDateFilters[row.company_name].startDate ||
            companyDateFilters[row.company_name].endDate);
        const isFiltered = hasFilter && companyFilteredData[row.company_name];

        return (
          <div className="text-center">
            <span
              className="badge"
              style={{
                fontSize: '15px',
                padding: '6px 10px',
                background: row.heygenMinutes > 0 ? '#007bff' : '#6c757d',
                color: 'white',
                border: isFiltered ? '2px solid #28a745' : 'none',
              }}
            >
              {row.heygenMinutes.toFixed(2)} min
              {isFiltered && ' *'}
            </span>
            <div>
              <p style={{ fontSize: '14px', marginTop: '10px', fontWeight: '600' }}>
                {row.heygenSessions} sessions
              </p>
            </div>
          </div>
        );
      },
    },
    {
      name: 'OpenAI Tokens',
      selector: row => row.openaiTokens,
      sortable: true,
      right: true,
      width: '200px',
      cell: row => {
        const hasFilter =
          companyDateFilters[row.company_name] &&
          (companyDateFilters[row.company_name].startDate ||
            companyDateFilters[row.company_name].endDate);
        const isFiltered = hasFilter && companyFilteredData[row.company_name];

        return (
          <div className="text-center">
            <span
              className="badge"
              style={{
                fontSize: '15px',
                padding: '6px 10px',
                background: row.openaiTokens > 0 ? '#17a2b8' : '#6c757d',
                color: 'white',
                border: isFiltered ? '2px solid #28a745' : 'none',
              }}
            >
              {row.openaiTokens.toLocaleString()} tokens
              {isFiltered && ' *'}
            </span>
            <div>
              <p style={{ fontSize: '14px', marginTop: '10px', fontWeight: '600' }}>
                {row.openaiRequests} requests
              </p>
            </div>
          </div>
        );
      },
    },
    {
      name: 'Monthly OpenAI Token Limit',
      selector: row => row.monthlyOpenAiTotalToken,
      sortable: true,
      right: true,
      width: '220px',
      cell: row => (
        <div className="text-center">
          <span
            className="badge"
            style={{
              fontSize: '15px',
              padding: '6px 10px',
              background: row.monthlyOpenAiTotalToken > 0 ? '#6f42c1' : '#6c757d',
              color: 'white',
            }}
          >
            {(row.monthlyOpenAiTotalToken || 0).toLocaleString()} tokens
          </span>
        </div>
      ),
    },
    {
      name: 'Live Streaming Minutes',
      selector: row => row.liveStreamingMint,
      sortable: true,
      right: true,
      width: '220px',
      cell: row => (
        <div className="text-center">
          <span
            className="badge"
            style={{
              fontSize: '15px',
              padding: '6px 10px',
              background: row.liveStreamingMint > 0 ? '#fd7e14' : '#6c757d',
              color: 'white',
            }}
          >
            {(row.liveStreamingMint || 0).toFixed(2)} min
          </span>
        </div>
      ),
    },
  ];

  // Columns for Heygen
  const heygenColumns = [
    {
      name: 'Date / Time',
      selector: row => formatTimestamp(row.updatedAt),
      sortable: true,
      width: '200px',
    },
    { name: 'Heygen Session ID', selector: row => row.session_id, sortable: true },
    { name: 'Total Minutes', selector: row => row.total_minutes, sortable: true },
    {
      name: 'Mode',
      selector: row => row.mode || 'Live Streaming',
      sortable: true,
      width: '150px',
      cell: row => (
        <span
          className="badge"
          style={{
            fontSize: '13px',
            padding: '5px 10px',
            background: row.mode === 'Interactive' ? '#6f42c1' : '#17a2b8',
            color: 'white',
          }}
        >
          {row.mode || 'Live Streaming'}
        </span>
      ),
    },
    { name: 'Client URL', selector: row => row.client_url, sortable: true },
  ];

  // Columns for OpenAI
  const openaiColumns = [
    {
      name: 'Date / Time',
      selector: row => formatTimestamp(row.updatedAt),
      sortable: true,
      width: '200px',
    },
    { name: 'OpenAI ID', selector: row => row.openai_id, sortable: true },
    { name: 'Model', selector: row => row.model, sortable: true },
    { name: 'Total Tokens', selector: row => row.total_tokens, sortable: true },
    { name: 'Client URL', selector: row => row.client_url, sortable: true },
  ];

  // CSV Data - use filtered data for summary
  const summaryCsv = filteredSummaryData.map(r => ({
    'Company Name': r.company_name,
    'Client URL': r.originalIdentifier,
    'Total Heygen Minutes': r.heygenMinutes.toFixed(2),
    'Total OpenAI Tokens': r.openaiTokens,
    'Monthly OpenAI Token Limit': r.monthlyOpenAiTotalToken || 0,
    'Live Streaming Minutes': (r.liveStreamingMint || 0).toFixed(2),
    'Heygen Sessions': r.heygenSessions,
    'OpenAI Requests': r.openaiRequests,
  }));

  const heygenCsv = heygenReports.map(r => ({
    'Date / Time': formatTimestamp(r.updatedAt),
    'Heygen Session ID': r.session_id,
    'Total Minutes': r.total_minutes,
    'Mode': r.mode || 'Live Streaming',
    'Client URL': r.client_url,

  }));

  const openaiCsv = openaiReports.map(r => ({
    'Date / Time': formatTimestamp(r.updatedAt),
    'OpenAI ID': r.openai_id,
    Model: r.model,
    'Total Tokens': r.total_tokens,
    'Client URL': r.client_url,
  }));

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px">
          <div className="dashboard-container">
            {/* Snackbar for error messages */}
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                {dateError}
              </Alert>
            </Snackbar>

            <div className="card-box mb-30">
              <div className="p-4 d-flex justify-content-between align-items-center">
                <div>
                  <h3>
                    {activeTab === 'summary' && 'Company Usage Summary'}
                    {activeTab === 'heygen' && 'Heygen Reports'}
                    {activeTab === 'openai' && 'OpenAI Reports'}
                  </h3>
                  <p className="mb-0 text-muted">
                    {activeTab === 'summary' &&
                      'Overview of all companies and their usage analytics'}
                    {activeTab === 'heygen' && 'Detailed Heygen session reports'}
                    {activeTab === 'openai' && 'Detailed OpenAI usage reports'}
                  </p>
                </div>

                {activeTab === 'summary' && (
                  <CSVLink
                    data={summaryCsv}
                    filename={'company-usage-summary.csv'}
                    className="btn btn-success"
                  >
                    Export Summary
                  </CSVLink>
                )}
                {activeTab === 'heygen' && (
                  <CSVLink
                    data={heygenCsv}
                    filename={'heygen-reports.csv'}
                    className="btn btn-success"
                  >
                    Export CSV
                  </CSVLink>
                )}
                {activeTab === 'openai' && (
                  <CSVLink
                    data={openaiCsv}
                    filename={'openai-reports.csv'}
                    className="btn btn-success"
                  >
                    Export CSV
                  </CSVLink>
                )}
              </div>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      color: 'text.primary',
                      transition: 'all 0.3s ease',
                      borderRadius: '8px 8px 0 0',
                      margin: '0 4px',
                      minHeight: '48px',
                      fontWeight: '600',
                      fontSize: '14px',
                      textTransform: 'none',
                      '&:hover': {
                        color: 'white',
                        backgroundColor: '#007bff',
                        boxShadow: '0 4px 12px rgba(0,123,255,0.3)',
                      },
                      '&.Mui-selected': {
                        color: 'white',
                        backgroundColor: '#0056b3',
                        fontWeight: 'bold',
                        borderBottom: '3px solid',
                        borderBottomColor: '#003d82',
                      },
                    },
                  }}
                >
                  <Tab label="Summary Dashboard" value="summary" />
                  <Tab label="Heygen Reports" value="heygen" />
                  <Tab label="OpenAI Reports" value="openai" />
                </Tabs>
              </Box>

              {/* Summary Tab Content */}
              {activeTab === 'summary' && (
                <>
                  {/* Summary Cards */}
                  <Grid container spacing={3} sx={{ mb: 3, display: 'none' }}>
                    {/* ... existing summary cards code ... */}
                  </Grid>

                  {/* Chart Type Selector */}
                  <Paper sx={{ p: 2, mb: 3, display: 'none' }}>
                    {/* ... existing chart selector code ... */}
                  </Paper>

                  {/* Charts */}
                  {filteredSummaryData.length > 0 && (
                    <Grid container spacing={3} sx={{ mb: 3, display: 'none' }}>
                      {/* ... existing charts code ... */}
                    </Grid>
                  )}

                  {/* Global Filter */}
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Global Date Range Filter
                    </Typography>
                    <Grid container spacing={2} alignItems="end">
                      <Grid item xs={12} sm={5} md={3}>
                        <label className="font-weight-600 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={tempSummaryStartDate}
                          onChange={e =>
                            handleStartDateChange(
                              e.target.value,
                              setTempSummaryStartDate,
                              setTempSummaryEndDate,
                              tempSummaryEndDate
                            )
                          }
                          className="form-control"
                        />
                      </Grid>
                      <Grid item xs={12} sm={5} md={3}>
                        <label className="font-weight-600 mb-1">End Date</label>
                        <input
                          type="date"
                          value={tempSummaryEndDate}
                          min={getMinEndDate(tempSummaryStartDate)}
                          onChange={e =>
                            handleEndDateChange(
                              e.target.value,
                              tempSummaryStartDate,
                              setTempSummaryEndDate
                            )
                          }
                          className="form-control"
                          style={{
                            border: dateError ? '1px solid red' : '1px solid #ced4da',
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2} md={2}>
                        <button
                          onClick={applyGlobalSummaryFilter}
                          className="btn btn-primary"
                          style={{ width: '100%', backgroundColor: '#142127' }}
                          disabled={!!dateError}
                        >
                          Apply
                        </button>
                      </Grid>
                      <Grid item xs={12} sm={12} md={4}>
                        <Typography variant="body2" color="textSecondary">
                          {summaryStartDate && summaryEndDate
                            ? `Showing data from ${summaryStartDate} to ${summaryEndDate}`
                            : 'Showing all available data'}
                        </Typography>
                        {(summaryStartDate || summaryEndDate) && (
                          <button
                            onClick={clearGlobalSummaryFilter}
                            className="btn btn-sm btn-outline-secondary mt-1"
                          >
                            Clear All Filters
                          </button>
                        )}
                      </Grid>
                    </Grid>
                    {dateError && (
                      <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        {dateError}
                      </Typography>
                    )}
                  </Paper>

                  {/* Companies Table */}
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      Companies Usage Details
                      {Object.keys(companyFilteredData).length > 0 && (
                        <small className="text-muted ml-2">
                          (* indicates filtered data for specific date range)
                        </small>
                      )}
                    </Typography>
                    {loading ? (
                      <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                        <p className="mt-2">Loading company usage data...</p>
                      </div>
                    ) : (
                      <DataTable
                        columns={summaryColumns}
                        data={filteredSummaryData}
                        pagination
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        paginationPerPage={25}
                        highlightOnHover
                        striped
                        responsive
                        customStyles={customStyles}
                        noDataComponent={
                          <div className="p-5 text-center">
                            <h5>No data available</h5>
                            <p className="text-muted">
                              Try adjusting your date filters or check if there are any reports in
                              the system.
                            </p>
                          </div>
                        }
                      />
                    )}
                  </Paper>
                </>
              )}

              {/* Detail Reports Filters */}
              {(activeTab === 'heygen' || activeTab === 'openai') && (
                <div className="row mb-3 px-3">
                  <div className="col-md-12">
                    <div className="card-box p-3" style={{ background: '#f8f9fa' }}>
                      <h5 className="mb-3">🔍 Filter Reports</h5>
                      <div className="row">
                        <div className="col-md-3">
                          <label className="font-weight-600 mb-2">Start Date</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={e =>
                              handleStartDateChange(
                                e.target.value,
                                setStartDate,
                                setEndDate,
                                endDate
                              )
                            }
                            className="form-control"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="font-weight-600 mb-2">End Date</label>
                          <input
                            type="date"
                            value={endDate}
                            min={getMinEndDate(startDate)}
                            onChange={e =>
                              handleEndDateChange(e.target.value, startDate, setEndDate)
                            }
                            className="form-control"
                            style={{
                              border: dateError ? '1px solid red' : '1px solid #ced4da',
                            }}
                          />
                          {dateError && (
                            <small className="text-danger" style={{ fontSize: '12px' }}>
                              {dateError}
                            </small>
                          )}
                        </div>
                        <div className="col-md-4">
                          <label className="font-weight-600 mb-2">Client URL</label>
                          <input
                            type="text"
                            placeholder="Search by Client URL"
                            value={clientUrl}
                            onChange={e => setClientUrl(e.target.value)}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Heygen Reports Table */}
              {activeTab === 'heygen' && (
                <DataTable
                  columns={heygenColumns}
                  data={heygenReports}
                  pagination
                  paginationRowsPerPageOptions={[50, 100, 150]}
                  paginationPerPage={50}
                  highlightOnHover
                  customStyles={customStyles}
                />
              )}

              {/* OpenAI Reports Table */}
              {activeTab === 'openai' && (
                <DataTable
                  columns={openaiColumns}
                  data={openaiReports}
                  pagination
                  paginationRowsPerPageOptions={[50, 100, 150]}
                  paginationPerPage={50}
                  highlightOnHover
                  customStyles={customStyles}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
